import { useCallback, useEffect, useRef, useState } from 'react'

// Spotify Player state interface
interface SpotifyPlayerState {
  playerId: string
  controller: any | null
  duration: number
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  error: string | null
  togglePlayPause: () => void
  seekTo: (_time: number) => void
  pause: () => void
}

// Declare Spotify Embed API types
declare global {
  interface Window {
    onSpotifyIframeApiReady: (_IFrameAPI: any) => void
  }
}

/**
 * Hook for managing Spotify player integration using iframe API
 * Provides audio playback controls and synchronization with waveforms
 */
// Global cache for Spotify API
let spotifyApiCache: any = null
let spotifyApiPromise: Promise<any> | null = null

// Debounce seek operations
let seekTimeout: NodeJS.Timeout | null = null
// Debounce toggle operations
let toggleTimeout: NodeJS.Timeout | null = null

export const useSpotifyPlayer = (
  trackId: string | null,
  onCleanup?: () => void, // Callback for external cleanup
): SpotifyPlayerState => {
  const [controller, setController] = useState<any>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const controllerRef = useRef<any>(null)
  const playerIdRef = useRef(`spotify-player-${Date.now()}`)
  const isInitializedRef = useRef(false)
  const currentTrackIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!trackId) {
      // Clean up when no trackId
      if (controllerRef.current) {
        try {
          controllerRef.current.destroy?.()
        } catch (e) {
          // Ignore cleanup errors
        }
        controllerRef.current = null
        setController(null)
        setIsReady(false)
      }

      // Call external cleanup callback
      if (onCleanup) {
        onCleanup()
      }

      isInitializedRef.current = false
      currentTrackIdRef.current = null
      return
    }

    let isMounted = true

    const loadSpotifyAPI = (): Promise<any> => {
      // Return cached API if available
      if (spotifyApiCache) {
        return Promise.resolve(spotifyApiCache)
      }

      // Return existing promise if already loading
      if (spotifyApiPromise) {
        return spotifyApiPromise
      }

      // Create new promise
      spotifyApiPromise = new Promise((resolve, reject) => {
        // Add script if not already present
        if (!document.querySelector('script[src*="spotify.com/embed"]')) {
          const script = document.createElement('script')
          script.src = 'https://open.spotify.com/embed/iframe-api/v1'
          script.async = true

          script.onerror = _e => {
            spotifyApiPromise = null // Reset promise on error
            reject(new Error('Failed to load Spotify API'))
          }

          document.head.appendChild(script)
        }

        // Poll interval local to this promise (self-cleaning)
        let pollInterval: NodeJS.Timeout | null = null

        // Set up callback for when API calls it
        window.onSpotifyIframeApiReady = (IFrameAPI: any) => {
          // Clear poll interval to avoid double-resolve
          if (pollInterval) {
            clearInterval(pollInterval)
            pollInterval = null
          }
          spotifyApiCache = IFrameAPI
          resolve(IFrameAPI)
        }

        // Poll as fallback (avoids race condition if callback already fired)
        let attempts = 0
        const maxAttempts = 50 // 5 seconds total
        pollInterval = setInterval(() => {
          attempts++
          if (spotifyApiCache) {
            if (pollInterval) {
              clearInterval(pollInterval)
              pollInterval = null
            }
            resolve(spotifyApiCache)
          } else if (attempts >= maxAttempts) {
            if (pollInterval) {
              clearInterval(pollInterval)
              pollInterval = null
            }
            spotifyApiPromise = null
            reject(new Error('Spotify API load timeout'))
          }
        }, 100)
      })

      return spotifyApiPromise
    }

    const initPlayer = async () => {
      try {
        // Prevent double initialization for the same track
        if (isInitializedRef.current && currentTrackIdRef.current === trackId) {
          // Reset player state when reusing existing player
          if (controllerRef.current) {
            try {
              controllerRef.current.pause() // Pause playback
              setCurrentTime(0)
              setIsPlaying(false)
            } catch (err) {
              // Ignore reset errors
            }
          }
          return
        }

        // If track changed, reset initialization
        if (currentTrackIdRef.current !== trackId) {
          // Clean up previous controller if it exists
          if (controllerRef.current) {
            try {
              controllerRef.current.destroy?.()
            } catch (e) {
              // Ignore cleanup errors
            }
            controllerRef.current = null
            setController(null)
            setIsReady(false)
          }
          setDuration(0)
          setCurrentTime(0)
          setIsPlaying(false)

          // Call external cleanup for previous track
          if (onCleanup) {
            onCleanup()
          }

          isInitializedRef.current = false
          currentTrackIdRef.current = trackId
        }

        const IFrameAPI = await loadSpotifyAPI()

        if (!isMounted) {
          return
        }

        // This handles race conditions when switching between player types
        const waitForDOMElement = async (
          elementId: string,
          maxRetries = 10,
          initialDelay = 100,
        ): Promise<HTMLElement | null> => {
          let delay = initialDelay
          for (let i = 0; i < maxRetries; i++) {
            await new Promise(resolve => setTimeout(resolve, delay))
            const element = document.getElementById(elementId)
            if (element) {
              return element
            }
            delay = Math.min(delay * 1.5, 500)
          }
          return null
        }

        const playerElement = await waitForDOMElement(playerIdRef.current)

        if (!playerElement) {
          throw new Error('Player DOM element not found')
        }

        if (!isMounted) {
          return
        }

        isInitializedRef.current = true // Mark as initialized

        IFrameAPI.createController(
          playerElement,
          {
            uri: `spotify:track:${trackId}`,
            width: '100%',
            height: '0',
            display: 'none',
            // Force preview mode for all users
            theme: 0,
            view: 'coverart',
            preview: true,
          },
          (EmbedController: any) => {
            if (!isMounted) return

            controllerRef.current = EmbedController
            setController(EmbedController)
            setIsReady(true)
            setDuration(30)

            // Listen for playback updates
            EmbedController.addListener('playback_update', (e: any) => {
              if (!isMounted) return

              const data = e.data
              if (data) {
                // Convert milliseconds to seconds
                const durationSec = data.duration ? data.duration / 1000 : 0
                const positionSec = data.position ? data.position / 1000 : 0
                const playing = !data.isPaused && !data.isBuffering

                // Force maximum 30 second duration for preview consistency
                const maxDuration = Math.min(durationSec, 30)

                // Pause if we exceed 30 seconds
                if (positionSec >= 30 && playing) {
                  EmbedController.pause()
                }

                setDuration(maxDuration)
                setCurrentTime(Math.min(positionSec, 30))
                setIsPlaying(playing && positionSec < 30)
              }
            })

            // Listen for ready state
            EmbedController.addListener('ready', () => {
              if (!isMounted) return
            })

            // Listen for errors
            EmbedController.addListener('error', (e: any) => {
              console.error('Spotify player error:', e)
              setError(
                'Unable to play this track. It may not be available for preview',
              )
            })
          },
        )
      } catch (err) {
        console.error('Failed to initialize Spotify player:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    initPlayer()

    return () => {
      isMounted = false
      isInitializedRef.current = false // Reset initialization flag
      currentTrackIdRef.current = null // Reset current track

      if (controllerRef.current) {
        try {
          // Clean up controller
          controllerRef.current.destroy?.()
          controllerRef.current = null
          setController(null)
          setIsReady(false)
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    }
  }, [onCleanup, trackId])

  const togglePlayPause = useCallback(() => {
    if (!controllerRef.current) {
      return
    }

    // Debounce toggle operations to prevent rapid clicks
    if (toggleTimeout) {
      return
    }

    toggleTimeout = setTimeout(() => {
      toggleTimeout = null
    }, 300) // 300ms debounce

    try {
      if (isPlaying) {
        controllerRef.current.pause()
      } else {
        // If we're at the end (30 seconds), force restart by seeking to 0 first
        if (currentTime >= 30) {
          // First pause to ensure clean state
          controllerRef.current.pause()
          // Seek to beginning
          controllerRef.current.seek(0)
          // Force play (not resume)
          setTimeout(() => {
            if (controllerRef.current) {
              controllerRef.current.play()
            }
          }, 100)
        } else {
          controllerRef.current.resume()
        }
      }
    } catch (err) {
      // Ignore toggle errors
    }
  }, [isPlaying, currentTime])

  const seekTo = useCallback(
    (time: number) => {
      if (!controllerRef.current) {
        return
      }

      // Clear any existing seek timeout to debounce
      if (seekTimeout) {
        clearTimeout(seekTimeout)
      }

      // Debounce seek operations
      seekTimeout = setTimeout(() => {
        if (!controllerRef.current) return

        // Bounds check - ensure seek time is valid (in seconds, not milliseconds)
        const seekTimeSeconds = Math.max(
          0,
          Math.min(Math.floor(time), Math.floor(duration)),
        )

        try {
          // Spotify API expects seconds as integer, not milliseconds
          controllerRef.current.seek(seekTimeSeconds)
          // Don't update React state immediately - let Spotify's playback_update handle it
        } catch (err) {
          // Ignore seek errors
        }
      }, 100) // 100ms debounce
    },
    [duration],
  )

  const pause = useCallback(() => {
    if (!controllerRef.current) {
      return
    }
    try {
      controllerRef.current.pause()
    } catch (err) {
      console.error('[useSpotifyPlayer] Pause error:', err)
    }
  }, [])

  return {
    playerId: playerIdRef.current,
    controller,
    duration,
    currentTime,
    isPlaying,
    isReady,
    error,
    togglePlayPause,
    seekTo,
    pause,
  }
}
